import useSubscription from '@src/hooks/useSubscription';
import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { generateDataPoint, generateTopics } from '@src/utils/generateTopic';
import { MqttClient } from 'mqtt';
import { useMemo } from 'react';

export interface PowinControllerPowerData {
  currentPset: string;
  currentQset: string;
  minPThreshold: number;
  maxPThreshold: number;
  minQThreshold: number;
  maxQThreshold: number;
}

const findValueByPointName = (
  dataPoints: DataPointWithValue[],
  pointName: string,
): DataPointWithValue['value'] => {
  const dataPoint = dataPoints.find(
    (dp) => dp.pointName === pointName || dp.pointAlias === pointName,
  );
  return dataPoint?.value;
};

const usePowinControllerPowerData = (
  mqttClient: MqttClient,
  siteMeta: SiteMeta,
  deviceId: string,
): PowinControllerPowerData => {
  const minPPointName = siteMeta.PowinAllowedMinP_PointName;
  const maxPPointName = siteMeta.PowinAllowedMaxP_PointName;
  const minQPointName = siteMeta.PowinAllowedMinQ_PointName;
  const maxQPointName = siteMeta.PowinAllowedMaxQ_PointName;

  const pointsToSubscribe = useMemo(() => {
    const siteDataPoints = generateTopics(
      [
        { deviceType: 'site', pointName: 'Pset' },
        { deviceType: 'site', pointName: 'Qset' },
        { deviceType: 'site', pointName: minPPointName },
        { deviceType: 'site', pointName: maxPPointName },
        { deviceType: 'site', pointName: minQPointName },
        { deviceType: 'site', pointName: maxQPointName },
      ],
      siteMeta.siteId,
    );

    const minMaxDataPoints: DataPoint[] = [
      minPPointName,
      maxPPointName,
      minQPointName,
      maxQPointName,
    ].map((pointName) =>
      generateDataPoint(
        {
          deviceType: 'tsc',
          controllerId: siteMeta.siteId,
          sourceDeviceId: deviceId,
          pointName,
          pointAlias: pointName,
        },
        siteMeta.siteId,
      ),
    );

    return [...siteDataPoints, ...minMaxDataPoints];
  }, [
    deviceId,
    siteMeta.siteId,
    minPPointName,
    maxPPointName,
    minQPointName,
    maxQPointName,
  ]);

  const dataPointsWithValue = useSubscription(pointsToSubscribe, mqttClient);

  return useMemo(() => {
    const psetValue = findValueByPointName(dataPointsWithValue, 'Pset');
    const qsetValue = findValueByPointName(dataPointsWithValue, 'Qset');
    const minPValue = findValueByPointName(dataPointsWithValue, minPPointName);
    const maxPValue = findValueByPointName(dataPointsWithValue, maxPPointName);
    const minQValue = findValueByPointName(dataPointsWithValue, minQPointName);
    const maxQValue = findValueByPointName(dataPointsWithValue, maxQPointName);

    return {
      currentPset: String(psetValue ?? ''),
      currentQset: String(qsetValue ?? ''),
      minPThreshold: Number(minPValue),
      maxPThreshold: Number(maxPValue),
      minQThreshold: Number(minQValue),
      maxQThreshold: Number(maxQValue),
    };
  }, [
    dataPointsWithValue,
    minPPointName,
    maxPPointName,
    minQPointName,
    maxQPointName,
  ]);
};

export default usePowinControllerPowerData;
